/**
 * Exibe a assinatura de aprovação capturada na página pública do orçamento.
 * A assinatura é gravada em `quote_approvals` pela função approve_quote_public()
 * e só o dono do orçamento consegue lê-la (RLS). Só renderiza algo quando o
 * orçamento está aprovado E existe assinatura.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PenLine } from 'lucide-react';
import { formatDocument } from '@/lib/documentUtils';

interface QuoteApprovalSignatureProps {
  quoteId: string;
  /** true quando o orçamento está aprovado/concluído. */
  approved: boolean;
}

export function QuoteApprovalSignature({ quoteId, approved }: QuoteApprovalSignatureProps) {
  const enabled = approved && Boolean(quoteId) && !String(quoteId).startsWith('offline_');

  const { data } = useQuery({
    queryKey: ['quote-approval', quoteId],
    enabled,
    queryFn: async () => {
      const { data } = await supabase
        .from('quote_approvals')
        .select('signature, approver_name, approver_document, approved_at')
        .eq('quote_id', quoteId)
        .maybeSingle();
      return data;
    },
  });

  if (!enabled || !data?.signature) return null;

  const meta: string[] = [];
  if (data.approver_name) meta.push(data.approver_name);
  if (data.approver_document) meta.push(formatDocument(data.approver_document));
  if (data.approved_at) {
    meta.push(`Aprovado em ${new Date(data.approved_at).toLocaleString('pt-BR')}`);
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <PenLine className="h-4 w-4" />
          Assinatura de aprovação
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="rounded-md border bg-white p-2">
          {/* A assinatura é um data URL (PNG) desenhado pelo cliente. */}
          <img
            src={data.signature}
            alt="Assinatura do cliente na aprovação"
            className="mx-auto max-h-32 w-auto"
          />
        </div>
        {meta.length > 0 && (
          <p className="text-xs text-muted-foreground">{meta.join(' · ')}</p>
        )}
      </CardContent>
    </Card>
  );
}
